import React, { useState, useEffect, useRef } from 'react';
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { flash_alert } from 'components/App';
import { useTranslation } from 'react-i18next';

// Material-UI Components
import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import CircularProgress from '@material-ui/core/CircularProgress';

import { csrf } from "constants/csrf";
import { calculateQuestionnaireStatus } from "utils/questionnaireUtils";

const useStyles = makeStyles((theme) => ({
    sectionPaper: {
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        fontSize: '16px',
        fontWeight: 'bold',
        marginBottom: theme.spacing(2),
        color: '#333',
    },
    requiredBadge: {
        display: 'inline-block',
        marginLeft: '8px',
        padding: '2px 8px',
        backgroundColor: '#e74c3c',
        color: 'white',
        fontSize: '11px',
        fontWeight: '600',
        borderRadius: '4px',
    },
    optionalBadge: {
        display: 'inline-block',
        marginLeft: '8px',
        padding: '2px 8px',
        backgroundColor: '#95a5a6',
        color: 'white',
        fontSize: '11px',
        fontWeight: '600',
        borderRadius: '4px',
    },
    questionBlock: {
        marginBottom: theme.spacing(3),
        paddingBottom: theme.spacing(2),
        borderBottom: '1px solid #e0e0e0',
    },
    questionText: {
        fontSize: '14px',
        fontWeight: '500',
        marginBottom: theme.spacing(1),
        color: '#555',
    },
    observationsField: {
        marginTop: theme.spacing(1),
    },
    charCounter: {
        fontSize: '12px',
        color: '#666',
        textAlign: 'right',
        marginTop: '4px',
    },
    charCounterLimit: {
        color: '#f44336',
    },
    savingIndicator: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        fontSize: '12px',
        color: '#666',
        marginTop: '8px',
    },
    statusBadge: {
        display: 'inline-block',
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '600',
        marginLeft: '12px',
    },
    statusPendiente: {
        backgroundColor: '#ffeaa7',
        color: '#d63031',
    },
    statusEnProgreso: {
        backgroundColor: '#74b9ff',
        color: '#0984e3',
    },
    statusCompletado: {
        backgroundColor: '#55efc4',
        color: '#00b894',
    },
}));

// Installation Questionnaire Structure
const INSTALLATION_QUESTIONNAIRE = {
    general: {
        title: "General",
        answerType: "quality", // Bien / Regular / Malo
        questions: [
            { id: 1, text: "Estado físico del empaque" },
            { id: 2, text: "Equipo en sitio de instalación" },
            { id: 3, text: "Dimensiones de nicho son correctas" }
        ]
    },
    electrical: {
        title: "Instalación Eléctrica",
        answerType: "yesno", // Sí / No
        questions: [
            { id: 4, text: "Contacto eléctrico estándar dedicado" },
            { id: 5, text: "Clavija correcta" },
            { id: 6, text: "Línea eléctrica dedicada, estable y con fusible separado" }
        ]
    },
    hydraulic: {
        title: "Instalación Hidráulica",
        answerType: "yesno",
        questions: [
            { id: 7, text: "Línea de agua dedicada y libre de corrosión" },
            { id: 8, text: "Válvula de cierre de agua accesible" },
            { id: 9, text: "Conexión de drenaje vertical con altura mínima" }
        ]
    },
    specific: {
        title: "Específico",
        answerType: "yesno",
        questions: [
            { id: 10, text: "Kit de apilamiento para lavadora / secadora" },
            { id: 11, text: "Ducto de ventilación / ventilación adecuada" },
            { id: 12, text: "Kit de unión para refrigerador / congelador" },
            { id: 13, text: "Panel se encuentra en sitio de instalación" },
            { id: 14, text: "Manijas se encuentran en sitio de instalación" }
        ]
    }
};

const MAX_OBSERVATION_LENGTH = 500;

function VisitInstallationQuestionnaire(props) {
    const { t } = useTranslation();
    const classes = useStyles();

    // State for all answers: { questionId: { answer: '', observations: '' } }
    const [answers, setAnswers] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const saveTimeoutRef = useRef(null);

    // Calculate status automatically when answers change
    const questionnaireStatus = calculateQuestionnaireStatus(answers);

    // Load saved answers when visit_customer_product changes
    useEffect(() => {
        if (props.visitCustomerProduct?.id) {
            loadSavedAnswers();
        } else {
            setAnswers({});
            setIsLoading(false);
        }
    }, [props.visitCustomerProduct?.id]);

    // Load saved answers from API
    async function loadSavedAnswers() {
        if (!props.visitCustomerProduct?.id) {
            setIsLoading(false);
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(`/api/v1/visit_customer_products/${props.visitCustomerProduct.id}/installation_questionnaire`);
            if (response.ok) {
                const data = await response.json();
                if (data.answers) {
                    setAnswers(data.answers);
                }
            }
        } catch (error) {
            console.error('Error loading questionnaire answers:', error);
        } finally {
            setIsLoading(false);
        }
    }

    // Auto-save function with debounce
    function autoSave(updatedAnswers) {
        if (props.readOnly) return;
        if (!props.visitCustomerProduct?.id) {
            console.warn('Cannot save: no visit_customer_product selected');
            return;
        }

        // Clear existing timeout
        if (saveTimeoutRef.current) {
            clearTimeout(saveTimeoutRef.current);
        }

        // Set new timeout for auto-save (1 second debounce)
        saveTimeoutRef.current = setTimeout(async () => {
            setIsSaving(true);
            try {
                const response = await fetch(`/api/v1/visit_customer_products/${props.visitCustomerProduct.id}/installation_questionnaire`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-Token': csrf
                    },
                    body: JSON.stringify({
                        visit_customer_product_id: props.visitCustomerProduct.id,
                        answers: updatedAnswers
                    })
                });

                if (response.ok) {
                    // Success - no need to show message on every save
                    console.log('Questionnaire auto-saved successfully');
                } else {
                    flash_alert("", "Error al guardar el cuestionario", "error");
                }
            } catch (error) {
                console.error('Error saving questionnaire:', error);
                flash_alert("", "Error al guardar el cuestionario", "error");
            } finally {
                setIsSaving(false);
            }
        }, 1000);
    }

    // Handle answer change
    function handleAnswerChange(questionId, answer) {
        const updatedAnswers = {
            ...answers,
            [questionId]: {
                ...answers[questionId],
                answer: answer,
                observations: answers[questionId]?.observations || ''
            }
        };
        setAnswers(updatedAnswers);
        autoSave(updatedAnswers);
    }

    // Handle observations change
    function handleObservationsChange(questionId, observations) {
        // Limit to MAX_OBSERVATION_LENGTH characters
        const trimmedObservations = observations.slice(0, MAX_OBSERVATION_LENGTH);

        const updatedAnswers = {
            ...answers,
            [questionId]: {
                ...answers[questionId],
                answer: answers[questionId]?.answer || '',
                observations: trimmedObservations
            }
        };
        setAnswers(updatedAnswers);
        autoSave(updatedAnswers);
    }

    // Get answer options based on section type
    function getAnswerOptions(answerType) {
        if (answerType === 'quality') {
            return [
                { value: 'Bien', label: 'Bien' },
                { value: 'Regular', label: 'Regular' },
                { value: 'Malo', label: 'Malo' }
            ];
        } else if (answerType === 'yesno') {
            return [
                { value: 'Sí', label: 'Sí' },
                { value: 'No', label: 'No' }
            ];
        }
        return [];
    }

    // Render a single question
    function renderQuestion(question, answerType) {
        const questionId = question.id;
        const currentAnswer = answers[questionId]?.answer || '';
        const currentObservations = answers[questionId]?.observations || '';
        const charCount = currentObservations.length;
        const isOverLimit = charCount > MAX_OBSERVATION_LENGTH;

        return (
            <Grid key={questionId} item xs={12} className={classes.questionBlock}>
                <div className={classes.questionText}>
                    {question.id}. {question.text}
                </div>

                <FormControl component="fieldset" disabled={props.readOnly}>
                    <RadioGroup
                        row
                        value={currentAnswer}
                        onChange={(e) => handleAnswerChange(questionId, e.target.value)}
                    >
                        {getAnswerOptions(answerType).map((option) => (
                            <FormControlLabel
                                key={option.value}
                                value={option.value}
                                control={<Radio color="primary" />}
                                label={option.label}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>

                <TextField
                    fullWidth
                    multiline
                    rows={2}
                    variant="outlined"
                    size="small"
                    placeholder="Observaciones (opcional)"
                    value={currentObservations}
                    onChange={(e) => handleObservationsChange(questionId, e.target.value)}
                    className={classes.observationsField}
                    disabled={props.readOnly}
                />
                <div className={`${classes.charCounter} ${isOverLimit ? classes.charCounterLimit : ''}`}>
                    {charCount} / {MAX_OBSERVATION_LENGTH} caracteres
                </div>
            </Grid>
        );
    }

    // Render a section
    function renderSection(sectionKey, section) {
        // Only the "general" section is required for completion
        const isRequired = sectionKey === 'general';

        return (
            <Grid key={sectionKey} item xs={12}>
                <Paper className={classes.sectionPaper} elevation={1}>
                    <div className={classes.sectionTitle}>
                        {section.title}
                        {isRequired && (
                            <span className={classes.requiredBadge}>REQUERIDO</span>
                        )}
                        {!isRequired && (
                            <span className={classes.optionalBadge}>OPCIONAL</span>
                        )}
                    </div>
                    <Grid container spacing={2}>
                        {section.questions.map((question) =>
                            renderQuestion(question, section.answerType)
                        )}
                    </Grid>
                </Paper>
            </Grid>
        );
    }

    // Show loading state
    if (isLoading) {
        return (
            <Grid container justifyContent="center" style={{ padding: '40px' }}>
                <CircularProgress />
            </Grid>
        );
    }

    // Show message if no product selected
    if (!props.visitCustomerProduct?.id) {
        return (
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <p className="service-subtitle">Cuestionario de Instalación</p>
                    <p className="visit-summary-2">Seleccione un producto para ver su cuestionario de instalación.</p>
                </Grid>
            </Grid>
        );
    }

    // Get status badge class based on current status
    const getStatusClass = () => {
        if (questionnaireStatus === 'Pendiente') return classes.statusPendiente;
        if (questionnaireStatus === 'En progreso') return classes.statusEnProgreso;
        if (questionnaireStatus === 'Completado') return classes.statusCompletado;
        return '';
    };

    return (
        <React.Fragment>
            <Grid container spacing={2}>
                <Grid item xs={12}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <p className="service-subtitle" style={{ marginBottom: 0 }}>
                            Cuestionario de Instalación
                        </p>
                        <span className={`${classes.statusBadge} ${getStatusClass()}`}>
                            {questionnaireStatus}
                        </span>
                    </div>
                    {isSaving && (
                        <div className={classes.savingIndicator}>
                            <CircularProgress size={16} />
                            <span>Guardando...</span>
                        </div>
                    )}
                </Grid>

                {Object.entries(INSTALLATION_QUESTIONNAIRE).map(([key, section]) =>
                    renderSection(key, section)
                )}
            </Grid>
        </React.Fragment>
    );
}

const structuredSelector = createStructuredSelector({});
const mapDispatchToProps = {};

export default connect(structuredSelector, mapDispatchToProps)(VisitInstallationQuestionnaire);
